import { FC } from 'react';
import { useIntl } from 'react-intl';
import cn from 'classnames';
import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createBemBlockBuilder } from '@ebsco-ui/ebsco-ui';

import { paginationMessages, sharedMessages } from '@app/translations';
import { ShapeWithClassName } from '@app/constants';

import './PreviousNextPagination.scss';

interface PreviousNextPaginationProps extends ShapeWithClassName {
  pageNumber: number;
  actualTotalRecords: number;
  limitedTotalRecords: number;
  loadingPageDirection?: PreviousNextNavigationDirection;
  onPrevious: () => void;
  onNext: () => void;
}

export type PreviousNextNavigationDirection = 'previous' | 'next';

const cnBem = createBemBlockBuilder(['previousNextPagination']);

export const PreviousNextPagination: FC<
  ShapeWithClassName<PreviousNextPaginationProps>
> = ({
  pageNumber,
  actualTotalRecords,
  limitedTotalRecords,
  className,
  loadingPageDirection,
  onPrevious,
  onNext,
}) => {
  const { $t, formatNumber } = useIntl();

  const isPreviousButtonDisabled =
    pageNumber === 1 || loadingPageDirection === 'previous';
  const isNextButtonDisabled =
    pageNumber === limitedTotalRecords || loadingPageDirection === 'next';

  return (
    <nav
      aria-label={$t(paginationMessages.pagination)}
      className={cn(cnBem(), className)}
    >
      <button
        className="eb-button eb-button--link"
        disabled={isPreviousButtonDisabled}
        type="button"
        onClick={onPrevious}
      >
        <FontAwesomeIcon icon={faChevronLeft} />
        {$t(sharedMessages.previous)}
      </button>
      {$t(sharedMessages.of, {
        number: formatNumber(pageNumber),
        total: formatNumber(actualTotalRecords),
      })}
      <button
        className="eb-button eb-button--link"
        disabled={isNextButtonDisabled}
        type="button"
        onClick={onNext}
      >
        {$t(sharedMessages.next)}
        <FontAwesomeIcon icon={faChevronRight} />
      </button>
    </nav>
  );
};
